import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { useLanguage } from '@/lib/i18n/LanguageContext';

interface FeatureModalProps {
  children: React.ReactNode;
  feature: 'holograms' | 'scannable' | 'shipping' | 'quality';
}

// Deep content for SEO - naturally integrated keywords
const featureContent = {
  holograms: {
    en: {
      title: 'Authentic-Looking Holograms',
      description: 'Our proprietary hologram technology sets Fake-ID.com apart from competitors.',
      content: [
        {
          heading: 'Multi-Layer Holographic Overlays',
          text: 'Each fake ID card features sophisticated multi-layer holographic overlays that shimmer and shift when tilted under light. Our fake holograms ID technology replicates the same optical effects found on authentic identification documents, creating a premium novelty product.',
        },
        {
          heading: 'State-Specific Designs',
          text: 'Our holographic security features are designed to match the visual appearance of real state and government IDs. The authentic holograms on our fake ID cards include rainbow effects, ghost images, and fine-line patterns that react to UV light.',
        },
        {
          heading: 'Quality Materials',
          text: 'We use premium holographic foil with high-definition printing to ensure your novelty ID looks professional. The holographic security features are embedded directly into the card, not applied as a sticker, for maximum durability.',
        },
      ],
    },
    de: {
      title: 'Authentisch wirkende Hologramme',
      description: 'Unsere proprietäre Hologramm-Technologie unterscheidet Fake-ID.com von Mitbewerbern.',
      content: [
        {
          heading: 'Mehrschichtige holografische Überlagerungen',
          text: 'Jede Fake-ID-Karte verfügt über raffinierte mehrschichtige holografische Überlagerungen, die schimmern und sich beim Kippen unter Licht verändern. Unsere Hologramm-Technologie repliziert die gleichen optischen Effekte, die auf echten Ausweisdokumenten zu finden sind.',
        },
        {
          heading: 'Länderspezifische Designs',
          text: 'Unsere holografischen Sicherheitsmerkmale sind so konzipiert, dass sie dem visuellen Erscheinungsbild echter staatlicher Ausweise entsprechen. Die authentisch wirkenden Hologramme umfassen Regenbogeneffekte, Geisterbilder und Feinstmuster.',
        },
        {
          heading: 'Hochwertige Materialien',
          text: 'Wir verwenden Premium-Hologrammfolie mit hochauflösendem Druck. Die holografischen Sicherheitsmerkmale sind direkt in die Karte eingebettet – nicht als Aufkleber – für maximale Haltbarkeit.',
        },
      ],
    },
  },
  scannable: {
    en: {
      title: 'Scannable Barcodes & QR Codes',
      description: 'All our novelty ID cards feature functional scanning technology.',
      content: [
        {
          heading: 'PDF417 Barcode Technology',
          text: 'Every scannable fake ID from our shop includes a fully functional PDF417 barcode on the back. This 2D barcode can be read by standard barcode readers and apps. Download the PDF417 app to test your novelty ID card\'s barcode yourself.',
        },
        {
          heading: 'Custom QR Codes',
          text: 'Our fake ID cards also feature custom QR codes that link to your personalized vCard data. When scanned, the QR code displays the name, address, and other details you entered during customization. Perfect for entertainment and cosplay purposes.',
        },
        {
          heading: 'Barcode ID Cards Standards',
          text: 'We follow the same barcode standards used by official identification documents. The scannable features on our novelty ID cards are designed to work with common reader applications, making them ideal for prop and entertainment use.',
        },
      ],
    },
    de: {
      title: 'Scannbare Barcodes & QR-Codes',
      description: 'Alle unsere Neuheits-Ausweiskarten verfügen über funktionale Scan-Technologie.',
      content: [
        {
          heading: 'PDF417 Barcode-Technologie',
          text: 'Jeder scannbare Ausweis aus unserem Shop enthält einen voll funktionsfähigen PDF417-Barcode auf der Rückseite. Dieser 2D-Barcode kann von Standard-Barcode-Lesegeräten und Apps gelesen werden. Laden Sie die PDF417-App herunter, um den Barcode selbst zu testen.',
        },
        {
          heading: 'Individuelle QR-Codes',
          text: 'Unsere Ausweiskarten verfügen auch über individuelle QR-Codes, die zu Ihren personalisierten vCard-Daten verlinken. Beim Scannen zeigt der QR-Code den Namen, die Adresse und andere Details an, die Sie bei der Anpassung eingegeben haben.',
        },
        {
          heading: 'Barcode-Standards',
          text: 'Wir folgen den gleichen Barcode-Standards, die von offiziellen Ausweisdokumenten verwendet werden. Die scannbaren Funktionen sind für gängige Reader-Anwendungen konzipiert und ideal für Requisiten und Unterhaltung.',
        },
      ],
    },
  },
  shipping: {
    en: {
      title: 'Discrete Worldwide Shipping',
      description: 'Fast and secure delivery to over 180 countries.',
      content: [
        {
          heading: 'Unmarked Packaging',
          text: 'All fake ID orders ship in plain, unmarked envelopes with no branding or indication of contents. Discrete shipping is standard – your privacy is our priority. The return address shows a generic business name.',
        },
        {
          heading: 'Worldwide Delivery Options',
          text: 'We offer fast fake ID shipping to all major markets including USA, UK, Canada, Germany, France, Spain, Australia, and 170+ other countries. Germany: 3–5 business days, Europe: 7–10 business days, International: 14+ business days. Order tracking coming soon.',
        },
        {
          heading: 'Tracking & Insurance (Coming Soon)',
          text: 'Order tracking is coming soon! Once available, every order will include a tracking number so you can monitor your shipment. Our discrete packaging protects against damage. If your fake ID order is lost or damaged in transit, we offer free replacement shipping.',
        },
      ],
    },
    de: {
      title: 'Diskreter weltweiter Versand',
      description: 'Schnelle und sichere Lieferung in über 180 Länder.',
      content: [
        {
          heading: 'Neutrale Verpackung',
          text: 'Alle Bestellungen werden in neutralen, unmarkierten Umschlägen ohne Branding oder Inhaltsangabe versendet. Diskreter Versand ist Standard – Ihre Privatsphäre hat Priorität. Die Absenderadresse zeigt einen generischen Firmennamen.',
        },
        {
          heading: 'Weltweite Lieferoptionen',
          text: 'Wir bieten schnellen Versand in alle wichtigen Märkte: USA, UK, Kanada, Deutschland, Frankreich, Spanien, Australien und 170+ weitere Länder. Voraussichtliche Lieferzeit: 7–14 Werktage. Express-Versand (3–5 Tage) verfügbar. Tatsächliche Zeiten können abweichen.',
        },
        {
          heading: 'Tracking & Versicherung (Kommt bald)',
          text: 'Die Sendungsverfolgung kommt bald! Sobald verfügbar, erhält jede Bestellung eine Sendungsverfolgungsnummer. Unsere diskrete Verpackung schützt vor Beschädigung. Bei Verlust oder Beschädigung während des Transports bieten wir kostenlosen Ersatzversand.',
        },
      ],
    },
  },
  quality: {
    en: {
      title: 'Premium Card Quality',
      description: 'Credit card grade materials and professional printing.',
      content: [
        {
          heading: 'Credit Card Thickness',
          text: 'All our premium fake ID cards are printed on 0.8mm PVC – the exact same thickness as real credit cards. This credit card quality ensures durability and an authentic feel. Each novelty ID card is waterproof and scratch-resistant.',
        },
        {
          heading: 'Professional Printing Technology',
          text: 'We use industry-leading retransfer printing technology for photo-realistic quality. Edge-to-edge printing ensures no white borders. Colors are vibrant and fade-resistant. Your photo is printed at 600 DPI for crystal-clear detail.',
        },
        {
          heading: 'Durable Construction',
          text: 'Our PVC cards are laminated with protective overlays that resist wear and fading. The premium fake ID construction means your novelty card will last for years. UV-protected inks prevent yellowing over time.',
        },
      ],
    },
    de: {
      title: 'Premium Kartenqualität',
      description: 'Kreditkarten-Materialien und professioneller Druck.',
      content: [
        {
          heading: 'Kreditkarten-Dicke',
          text: 'Alle unsere Premium-Ausweiskarten werden auf 0,8 mm PVC gedruckt – die exakt gleiche Dicke wie echte Kreditkarten. Diese Kreditkartenqualität gewährleistet Haltbarkeit und ein authentisches Gefühl. Jede Karte ist wasserfest und kratzfest.',
        },
        {
          heading: 'Professionelle Drucktechnologie',
          text: 'Wir verwenden branchenführende Retransfer-Drucktechnologie für fotorealistische Qualität. Randloser Druck sorgt für keine weißen Ränder. Farben sind leuchtend und lichtbeständig. Ihr Foto wird mit 600 DPI gedruckt.',
        },
        {
          heading: 'Langlebige Konstruktion',
          text: 'Unsere PVC-Karten sind mit Schutzschichten laminiert, die Verschleiß und Verblassen widerstehen. Die Premium-Konstruktion bedeutet, dass Ihre Karte jahrelang hält. UV-geschützte Tinten verhindern Vergilbung.',
        },
      ],
    },
  },
};

export const FeatureModal: React.FC<FeatureModalProps> = ({ children, feature }) => {
  const { language } = useLanguage();
  
  // Fallback to English if language not available
  const lang = (language === 'en' || language === 'de') ? language : 'en';
  const content = featureContent[feature][lang];

  return (
    <Dialog>
      <DialogTrigger asChild>
        {children}
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl md:text-3xl font-display font-black uppercase tracking-tight">
            {content.title}
          </DialogTitle>
          <DialogDescription className="text-base text-muted-foreground">
            {content.description}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-6 pt-2">
          {content.content.map((section, index) => (
            <div key={index} className="space-y-2 border-l-2 border-primary/30 pl-5">
              <h3 className="text-base font-display font-bold text-foreground uppercase tracking-wide">
                {section.heading}
              </h3>
              <p className="text-muted-foreground text-sm leading-relaxed">
                {section.text}
              </p>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};
